import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, DownloadSimple, Presentation } from '@phosphor-icons/react';

const slides = [
  // Slide 1: Title
  {
    content: (
      <div className="flex flex-col items-center justify-center h-full text-center px-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold mb-8" style={{ background: '#c8f060', color: '#080808' }}>S</div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>ScalitOS</h1>
        <p className="text-xl sm:text-2xl max-w-2xl leading-relaxed" style={{ color: 'rgba(245,240,232,0.6)' }}>
          The marketing operating system for solo founders who don't have a marketing team.
        </p>
        <div className="mt-12 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full" style={{ background: '#c8f060' }} />
          <span className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>Pre-Seed · €75,000 · 2026</span>
        </div>
      </div>
    ),
  },
  // Slide 2: Problem
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>THE PROBLEM</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
          29.8 million solopreneurs in the US alone. Most have no marketing system.
        </h2>
        <p className="text-sm mb-8 max-w-3xl" style={{ color: 'rgba(245,240,232,0.4)' }}>
          81.9% of US small businesses are solo-owned with no employees (Bureau of Labor Statistics, 2026). They handle every function — including marketing — alone.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { stat: '29.8M', label: 'Solo businesses in the US', source: 'Founder Reports, 2026' },
            { stat: '35%', label: 'report high stress from doing everything alone', source: 'Solo Business Hub, 2026' },
            { stat: '74%', label: 'already use AI tools but lack a unified system', source: 'Solopreneur Statistics, 2026' },
          ].map((s, i) => (
            <div key={i} className="p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-3xl font-bold mb-2" style={{ color: '#c8f060' }}>{s.stat}</p>
              <p className="text-sm leading-relaxed mb-2" style={{ color: 'rgba(245,240,232,0.5)' }}>{s.label}</p>
              <p className="text-[9px] italic" style={{ color: 'rgba(245,240,232,0.2)' }}>{s.source}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // Slide 3: Why This Matters
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>WHY THIS MATTERS</p>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-10" style={{ fontFamily: "'Instrument Serif', serif" }}>
          The tools exist. The orchestration layer doesn't.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { title: 'Fragmented tool landscape', desc: 'Solopreneurs pay $100-500/month across 5-8 disconnected tools (Canva, Buffer, Mailchimp, ChatGPT). None tell them what to do each day.', source: 'PrometAI Solopreneur Tech Stack Report, 2026' },
            { title: 'AI adoption without direction', desc: '74% of solopreneurs use AI tools in 2026 (up from 66% in 2025), but most use them reactively — not as part of a coherent daily system.', source: 'Solo Business Hub Statistics, 2026' },
            { title: 'Consistency is the #1 barrier', desc: 'Social platforms reward daily presence. But without a plan, founders post sporadically and see zero growth — then quit within 90 days.', source: 'Meta Business Suite engagement data' },
            { title: 'Enterprise tools ignore this market', desc: 'The $29.7B SMB marketing software market is built for teams with budgets. Nobody builds an integrated system for one person at $19/month.', source: 'Business Research Insights, 2026' },
          ].map((point, i) => (
            <div key={i} className="p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-sm font-bold mb-2" style={{ color: '#f5f0e8' }}>{point.title}</p>
              <p className="text-xs leading-relaxed mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>{point.desc}</p>
              <p className="text-[9px] italic" style={{ color: 'rgba(245,240,232,0.15)' }}>{point.source}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // Slide 4: Solution
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>THE SOLUTION</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
          One system. Four screens. Daily clarity.
        </h2>
        <p className="text-base mb-8 max-w-3xl" style={{ color: 'rgba(245,240,232,0.4)' }}>ScalitOS replaces 5-8 disconnected tools with a single operating system that tells founders what to do, writes it for them, and tracks what's working.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { title: 'Daily Command Centre', why: 'Why it matters:', desc: 'Eliminates decision fatigue. Founders waste 4+ hours/week deciding what to work on. ScalitOS gives them 3 ranked daily priorities specific to their business, goals, and stage.', diff: 'No other tool provides a personalised daily marketing plan.' },
            { title: 'Content Engine', why: 'Why it matters:', desc: 'Solves the blank page problem. Generates captions, emails, ad copy, scripts, and carousels in the founder\'s own brand voice — not generic templates.', diff: '8 content formats + voice learning from every edit.' },
            { title: 'Growth Intelligence', why: 'Why it matters:', desc: 'Makes competitor tracking accessible. Tracks up to 5 competitors\' ads, social activity, and content strategy — previously only available in enterprise tools costing $500+/month.', diff: 'Live competitor data at $19/month vs $500+/month alternatives.' },
            { title: 'Marketing Advisor', why: 'Why it matters:', desc: 'Provides context-aware guidance. Unlike generic chatbots, the advisor knows the founder\'s business, audience, history, and competitors — giving specific, actionable answers.', diff: 'Persistent memory of your business across sessions.' },
          ].map((f, i) => (
            <div key={i} className="p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-sm font-bold mb-2" style={{ color: '#f5f0e8' }}>{f.title}</p>
              <p className="text-xs leading-relaxed mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>{f.desc}</p>
              <p className="text-[10px] font-medium" style={{ color: '#c8f060' }}>{f.diff}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // Slide 5: What Makes It Different
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>COMPETITIVE ADVANTAGE</p>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-8" style={{ fontFamily: "'Instrument Serif', serif" }}>
          Why ScalitOS wins: System vs. Tool
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                {['Capability', 'ScalitOS', 'Canva', 'Buffer', 'ChatGPT', 'Jasper'].map(h => (
                  <th key={h} className="px-3 py-3 text-[10px] uppercase tracking-wider font-bold" style={{ color: h === 'ScalitOS' ? '#c8f060' : 'rgba(245,240,232,0.3)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { feat: 'Personalised daily plan', vals: ['Yes', 'No', 'No', 'No', 'No'] },
                { feat: 'Learns your brand voice', vals: ['Yes', 'No', 'No', 'Manual', 'Partial'] },
                { feat: 'Live competitor tracking', vals: ['Yes', 'No', 'No', 'No', 'No'] },
                { feat: 'Multi-format content', vals: ['8 types', 'Design', 'No', 'Text only', 'Text only'] },
                { feat: 'Video ad creation', vals: ['Yes', 'No', 'No', 'No', 'No'] },
                { feat: 'Remembers your business', vals: ['Always', 'No', 'No', 'No', 'Partial'] },
                { feat: 'Built for solopreneurs', vals: ['Core focus', 'Teams', 'Teams', 'General', 'Marketers'] },
                { feat: 'Price for target user', vals: ['$19/mo', '$120/yr', '$36/mo', '$20/mo', '$49/mo'] },
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td className="px-3 py-2 text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>{row.feat}</td>
                  {row.vals.map((v, j) => (
                    <td key={j} className="px-3 py-2 text-xs text-center" style={{ color: j === 0 ? '#c8f060' : 'rgba(245,240,232,0.35)' }}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs mt-6 text-center" style={{ color: 'rgba(245,240,232,0.3)' }}>
          Our moat: Personalised daily orchestration + competitor intelligence + progressive voice learning. No competitor combines all three for solopreneurs.
        </p>
      </div>
    ),
  },
  // Slide 6: Market Size
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>MARKET OPPORTUNITY</p>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
          A $29.7B market built for teams. We're building for the individual.
        </h2>
        <p className="text-xs mb-8" style={{ color: 'rgba(245,240,232,0.3)' }}>Source: Business Research Insights (2026), Bureau of Labor Statistics, Solo Business Hub</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <div className="p-6 rounded-xl text-center" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.1)' }}>
            <p className="text-4xl font-bold mb-2" style={{ color: '#c8f060' }}>$29.7B</p>
            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'rgba(245,240,232,0.3)' }}>TAM</p>
            <p className="text-xs mt-2" style={{ color: 'rgba(245,240,232,0.3)' }}>Global SMB marketing software (2026)</p>
            <p className="text-[9px] mt-1 italic" style={{ color: 'rgba(245,240,232,0.15)' }}>Business Research Insights</p>
          </div>
          <div className="p-6 rounded-xl text-center" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.1)' }}>
            <p className="text-4xl font-bold mb-2" style={{ color: '#c8f060' }}>$6.8B</p>
            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'rgba(245,240,232,0.3)' }}>SAM</p>
            <p className="text-xs mt-2" style={{ color: 'rgba(245,240,232,0.3)' }}>Solo operators & micro-businesses (29.8M × $228/yr avg spend)</p>
            <p className="text-[9px] mt-1 italic" style={{ color: 'rgba(245,240,232,0.15)' }}>Bottom-up: BLS data × avg tool spend</p>
          </div>
          <div className="p-6 rounded-xl text-center" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.1)' }}>
            <p className="text-4xl font-bold mb-2" style={{ color: '#c8f060' }}>$1.4M</p>
            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'rgba(245,240,232,0.3)' }}>SOM (Year 1)</p>
            <p className="text-xs mt-2" style={{ color: 'rgba(245,240,232,0.3)' }}>500 paid users × $228/yr ARPU</p>
            <p className="text-[9px] mt-1 italic" style={{ color: 'rgba(245,240,232,0.15)' }}>Conservative: 3% free-to-paid conversion</p>
          </div>
        </div>
        <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.35)' }}>
            <strong style={{ color: '#f5f0e8' }}>Bottom-up calculation:</strong> 29.8M US solopreneurs → target 10% who actively market online (2.98M) → reach 0.5% in Year 1 (14,900 free users) → 3% convert to paid (500 users) → $19/mo avg = $114K ARR. With EU expansion: 2x multiplier.
          </p>
        </div>
      </div>
    ),
  },
  // Slide 7: Business Model
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>BUSINESS MODEL</p>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-8" style={{ fontFamily: "'Instrument Serif', serif" }}>
          Freemium SaaS with usage-based credits
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { tier: 'Starter', price: 'Free', credits: '20 credits/mo', desc: 'Daily plan, basic content, 1 competitor' },
            { tier: 'Growth', price: '$19/mo', credits: '50 credits/mo', desc: 'Full content engine, 5 competitors, campaigns', highlight: true },
            { tier: 'Pro', price: '$29/mo', credits: '100 credits/mo', desc: 'Video ads, priority queue, strategy reports' },
          ].map((p, i) => (
            <div key={i} className="p-5 rounded-xl" style={{ background: p.highlight ? 'rgba(200,240,96,0.04)' : 'rgba(255,255,255,0.02)', border: `1px solid ${p.highlight ? 'rgba(200,240,96,0.15)' : 'rgba(255,255,255,0.06)'}` }}>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{p.tier}</p>
              <p className="text-2xl font-bold mb-1" style={{ color: p.highlight ? '#c8f060' : '#f5f0e8' }}>{p.price}</p>
              <p className="text-xs mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>{p.credits}</p>
              <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.25)' }}>{p.desc}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Target ARPU', value: '€19', note: 'Growth tier' },
            { label: 'Free → Paid target', value: '3-5%', note: 'Industry avg: 2-5% (ChartMogul)' },
            { label: 'Gross Margin', value: '~75%', note: 'LLM API costs ~25%' },
            { label: 'Payback Period', value: '< 3 mo', note: 'Organic acquisition' },
          ].map((m, i) => (
            <div key={i} className="text-center p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <p className="text-lg font-bold" style={{ color: '#f5f0e8' }}>{m.value}</p>
              <p className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>{m.label}</p>
              <p className="text-[8px] mt-0.5 italic" style={{ color: 'rgba(245,240,232,0.15)' }}>{m.note}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // Slide 8: Traction
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>TRACTION & PROGRESS</p>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-8" style={{ fontFamily: "'Instrument Serif', serif" }}>
          Full product built. Live in production. Validating PMF.
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {[
            { value: 'Live', label: 'Product Status', sub: 'scalitos.com — fully deployed' },
            { value: '8 weeks', label: 'Build Time', sub: 'Solo founder, zero external devs' },
            { value: '15+', label: 'Features Shipped', sub: 'Content, video, ads, intelligence' },
            { value: 'Active', label: 'Stripe Payments', sub: 'Live billing + webhook processing' },
            { value: '5', label: 'Early Users', sub: 'Organic signups, no paid marketing' },
            { value: '<3 min', label: 'Time to Value', sub: 'From signup to first daily plan' },
          ].map((m, i) => (
            <div key={i} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-xl font-bold" style={{ color: '#c8f060' }}>{m.value}</p>
              <p className="text-xs font-semibold mt-1" style={{ color: '#f5f0e8' }}>{m.label}</p>
              <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>{m.sub}</p>
            </div>
          ))}
        </div>
        <div className="p-4 rounded-xl" style={{ background: 'rgba(200,240,96,0.02)', border: '1px solid rgba(200,240,96,0.08)' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#f5f0e8' }}>What's Built & Working</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
            {[
              'Daily personalised marketing plan',
              'Content creation (8 formats)',
              'Video ad generation (Sora 2)',
              'Carousel & flyer design engine',
              'Competitor intelligence tracking',
              'Brand voice analysis & matching',
              'Email campaign builder',
              'Marketing advisor (contextual)',
              'Stripe subscription billing',
              'Email verification (Resend)',
              'Team collaboration + sharing',
              'Admin dashboard + analytics',
            ].map((m, i) => (
              <p key={i} className="text-[10px] flex items-start gap-1.5" style={{ color: 'rgba(245,240,232,0.4)' }}>
                <span style={{ color: '#c8f060' }}>&#10003;</span> {m}
              </p>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  // Slide 9: Go-to-Market
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>GO-TO-MARKET STRATEGY</p>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-8" style={{ fontFamily: "'Instrument Serif', serif" }}>
          Product-led growth. Community-first acquisition.
        </h2>
        <div className="space-y-4">
          {[
            { phase: 'Now → Month 6', title: 'Community & Organic', budget: '€5K', items: ['Daily "What I\'d post today" content series (Twitter/X, LinkedIn)', 'Founder communities: Indie Hackers, Reddit r/entrepreneur, r/smallbusiness', 'Free tier as viral driver — 20 credits/month gives real value', 'SEO content targeting "marketing plan for small business" keywords'], target: '2,000 free users → 60-100 paid (3-5% conversion)' },
            { phase: 'Month 6 → 12', title: 'Paid + Partnerships', budget: '€15K', items: ['Meta & Google ads targeting "small business marketing help"', 'Partnerships with business coaches and course creators (rev share)', 'Content repurposing: turn user-generated content into case studies'], target: '10,000 free users → 300-500 paid' },
            { phase: 'Month 12 → 18', title: 'Scale & Expand', budget: '€20K', items: ['Shopify/Wix app store listings', 'Affiliate programme (20% rev share, first year)', 'Localisation for EU markets (DE, FR, ES)'], target: '25,000 free users → 750-1,250 paid' },
          ].map((p, i) => (
            <div key={i} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060' }}>{p.phase}</span>
                <span className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{p.title}</span>
                <span className="text-[9px] ml-auto px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.3)' }}>{p.budget} budget</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 mb-2">
                {p.items.map((item, j) => (
                  <p key={j} className="text-[10px]" style={{ color: 'rgba(245,240,232,0.35)' }}>• {item}</p>
                ))}
              </div>
              <p className="text-[10px] font-medium" style={{ color: '#c8f060' }}>Target: {p.target}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  // Slide 10: Team
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>THE TEAM</p>
        <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-10" style={{ fontFamily: "'Instrument Serif', serif" }}>
          Built by a founder who understands the problem firsthand.
        </h2>
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="w-24 h-24 rounded-2xl flex items-center justify-center text-3xl font-bold shrink-0" style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.15)' }}>
            FM
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-1" style={{ color: '#f5f0e8' }}>Favour Chukwuiso Malagu</h3>
            <p className="text-sm font-medium mb-4" style={{ color: '#c8f060' }}>Founder & CEO</p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(245,240,232,0.5)' }}>
              International Business graduate focused on product development and brand growth. Founder of HOOGLAM and ScalitOS — building and shipping real products that solve marketing challenges for solo founders. Experienced in using modern development tools to move from idea to production in weeks, not months.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.linkedin.com/in/favour-malagu" target="_blank" rel="noopener noreferrer" className="text-xs font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
                LinkedIn &#8599;
              </a>
              <a href="https://scalitos.com" target="_blank" rel="noopener noreferrer" className="text-xs font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
                scalitos.com &#8599;
              </a>
            </div>
          </div>
        </div>
        <div className="mt-10 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#f5f0e8' }}>Why This Founder</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              'Experienced the solopreneur marketing problem firsthand across multiple ventures',
              'Built the entire product from zero to production in 8 weeks — proving execution speed',
              'International Business background provides market understanding across EU and US',
            ].map((p, i) => (
              <p key={i} className="text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.35)' }}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  // Slide 11: The Ask
  {
    content: (
      <div className="flex flex-col justify-center h-full px-8 lg:px-16 max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#c8f060' }}>THE ASK</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-8" style={{ fontFamily: "'Instrument Serif', serif" }}>
          Raising €75,000 pre-seed.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div className="p-5 rounded-xl" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.1)' }}>
            <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: 'rgba(245,240,232,0.3)' }}>Use of Funds (€75K)</p>
            <div className="space-y-3">
              {[
                { pct: '29%', amount: '€22K', use: 'Product & Engineering', detail: 'API integrations, mobile app, feature iteration' },
                { pct: '24%', amount: '€18K', use: 'AI & Infrastructure', detail: 'LLM API costs, server scaling, data storage' },
                { pct: '20%', amount: '€15K', use: 'User Acquisition', detail: 'Paid ads, community, content marketing' },
                { pct: '13%', amount: '€10K', use: 'Legal & Operations', detail: 'Company formation, compliance, tooling' },
                { pct: '13%', amount: '€10K', use: 'Design & Contingency', detail: 'UX improvements, unexpected costs, buffer' },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-xs font-bold shrink-0 w-8" style={{ color: '#c8f060' }}>{f.pct}</span>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: '#f5f0e8' }}>{f.use} <span style={{ color: 'rgba(245,240,232,0.25)' }}>({f.amount})</span></p>
                    <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{f.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: 'rgba(245,240,232,0.3)' }}>12-Month Milestones</p>
            <div className="space-y-3">
              {[
                { metric: '10,000+', label: 'Free registered users' },
                { metric: '300-500', label: 'Paying subscribers' },
                { metric: '€5,700-9,500', label: 'Monthly recurring revenue' },
                { metric: '€68K-114K', label: 'Annual run rate (ARR)' },
                { metric: '3-5%', label: 'Free-to-paid conversion rate' },
              ].map((t, i) => (
                <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span className="text-[11px]" style={{ color: 'rgba(245,240,232,0.4)' }}>{t.label}</span>
                  <span className="text-sm font-bold" style={{ color: '#f5f0e8' }}>{t.metric}</span>
                </div>
              ))}
            </div>
            <p className="text-[9px] mt-3 italic" style={{ color: 'rgba(245,240,232,0.2)' }}>
              Based on 3-5% freemium conversion (industry benchmark: ChartMogul SaaS Conversion Report)
            </p>
          </div>
        </div>
      </div>
    ),
  },
  // Slide 12: Close
  {
    content: (
      <div className="flex flex-col items-center justify-center h-full text-center px-8">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold mb-8" style={{ background: '#c8f060', color: '#080808' }}>S</div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
          29.8 million founders need a marketing system.<br />We're building it.
        </h2>
        <p className="text-base max-w-xl mb-10" style={{ color: 'rgba(245,240,232,0.5)' }}>
          ScalitOS makes consistent marketing effortless for the solo founders doing it alone. The product is live. The market is ready. We need fuel to grow.
        </p>
        <div className="space-y-3">
          <p className="text-lg font-semibold" style={{ color: '#f5f0e8' }}>scalitos.com</p>
          <p className="text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>Favour Malagu — Founder & CEO</p>
          <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>favour@scalitos.com</p>
          <a href="https://www.linkedin.com/in/favour-malagu" target="_blank" rel="noopener noreferrer" className="inline-block text-xs font-medium px-4 py-2 rounded-full mt-2" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
            Connect on LinkedIn &#8599;
          </a>
        </div>
      </div>
    ),
  },
];

export default function PitchDeckPage() {
  const [current, setCurrent] = useState(0);
  const deckRef = useRef(null);

  const goNext = () => setCurrent(c => Math.min(c + 1, slides.length - 1));
  const goPrev = () => setCurrent(c => Math.max(c - 1, 0));

  const handleDownload = () => {
    // Download PPTX file
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_BACKEND_URL}/api/download/pitch-deck`;
    link.download = 'ScalitOS_Pitch_Deck.pptx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-page { page-break-after: always; height: 100vh; display: flex !important; }
        }
      `}</style>

      <div ref={deckRef} className="h-screen w-screen overflow-hidden relative" style={{ background: '#080808', color: '#f5f0e8' }}>
        {/* Slide (screen view) */}
        <div className="print:hidden h-full">
          <AnimatePresence mode="wait">
            <motion.div key={current} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}
              className="h-full w-full">
              {slides[current].content}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Print view: all slides stacked */}
        <div className="hidden print:block">
          {slides.map((slide, i) => (
            <div key={i} className="print-page" style={{ background: '#080808', color: '#f5f0e8', minHeight: '100vh' }}>
              {slide.content}
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="no-print fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 px-5 py-2.5 rounded-full z-50" style={{ background: 'rgba(20,20,20,0.9)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}>
          <button onClick={goPrev} disabled={current === 0} className="p-1.5 rounded-lg transition-opacity disabled:opacity-20" style={{ color: '#f5f0e8' }}>
            <ArrowLeft size={16} />
          </button>
          <span className="text-xs font-medium tabular-nums" style={{ color: 'rgba(245,240,232,0.4)' }}>{current + 1} / {slides.length}</span>
          <button onClick={goNext} disabled={current === slides.length - 1} className="p-1.5 rounded-lg transition-opacity disabled:opacity-20" style={{ color: '#f5f0e8' }}>
            <ArrowRight size={16} />
          </button>
          <div className="w-px h-4 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <button onClick={handleDownload} title="Download as PDF" className="p-1.5 rounded-lg transition-all hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.5)' }}>
            <DownloadSimple size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="no-print fixed top-0 left-0 right-0 h-0.5 z-50">
          <div className="h-full transition-all duration-300" style={{ width: `${((current + 1) / slides.length) * 100}%`, background: '#c8f060' }} />
        </div>
      </div>
    </>
  );
}
